/**
 * Newsletter Service
 * 
 * Handles newsletter subscription submissions to the backend API.
 * 
 * Following the established patterns:
 * - Uses the shared callApi function for consistent error handling
 * - Uses import.meta.env for environment variables (VITE_API_SERVER_URL)
 * - Returns ApiResponse<T> for consistent response format
 * - No authentication required (public endpoint)
 */

import { ApiResponse } from '../models/api-response';
import { callApi } from './api.service';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

/**
 * Interface for the newsletter subscription API response
 */
export interface NewsletterSubscriptionResponse {
  success: boolean;
  message: string;
}

/**
 * Interface for newsletter subscription request data
 */
export interface NewsletterSubscriptionRequest {
  email: string;
}

/**
 * Submit newsletter subscription to the backend
 * 
 * @param email - The email address to subscribe
 * @returns Promise<ApiResponse<NewsletterSubscriptionResponse>> - The API response
 */
export async function subscribeToNewsletter(email: string): Promise<ApiResponse<NewsletterSubscriptionResponse>> {
  // Prepare request data
  const requestData: NewsletterSubscriptionRequest = {
    email: email.trim().toLowerCase(),
  };
  
  // Use the shared API helper
  return callApi<NewsletterSubscriptionResponse>(`${apiServerUrl}/api/newsletter/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
}
